import { DateTime } from "luxon";
import { BlockTime } from "@/app/types/BlockTime";
import { DayOfWeek } from "@/app/types/ClinicHours";
import { DAY_ORDER } from "@/app/lib/days";

/** Long Slovak date from an ISO string, e.g. "31. júla 2026". */
export function formatSkDate(iso: string): string {
  return new Date(iso).toLocaleDateString("sk-SK", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Bratislava",
  });
}

export function formatSkAppointmentLuxon(
  iso: string,
  tz = "Europe/Bratislava",
) {
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(tz).setLocale("sk");
  const s = dt.toFormat("cccc, d. LLLL yyyy, HH:mm");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const BLOCK_TIME_LABEL = "Blokovaný čas";

/**
 * Calendar title of a blocked time: its title, then what it is — the same
 * shape as an appointment's "patient – visit type".
 */
export function blockTimeEventTitle(title?: string | null): string {
  const t = title?.trim();
  return t && t !== BLOCK_TIME_LABEL
    ? `${t} – ${BLOCK_TIME_LABEL}`
    : BLOCK_TIME_LABEL;
}

export function formatBlockTimeRange(
  bt: BlockTime,
  tz = "Europe/Bratislava",
): string {
  const start = DateTime.fromISO(bt.start_utc, { zone: "utc" }).setZone(tz);
  const end = DateTime.fromISO(bt.end_utc, { zone: "utc" }).setZone(tz);

  const isSameDay = start.hasSame(end, "day");

  if (bt.all_day) {
    if (isSameDay) {
      return start.toFormat("d.M.yyyy");
    } else {
      return `${start.toFormat("d.M.yyyy")} - ${end.toFormat("d.M.yyyy")}`;
    }
  } else {
    if (isSameDay) {
      return `${start.toFormat("d.M.yyyy HH:mm")} - ${end.toFormat("HH:mm")}`;
    } else {
      return `${start.toFormat("d.M.yyyy HH:mm")} - ${end.toFormat(
        "d.M.yyyy HH:mm",
      )}`;
    }
  }
}

export function fmtYMD(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}
export function fmtYM(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export const normalizeIso = (s: string) => new Date(s).toISOString(); // "Z" form

const DEFAULT_MIN_HOUR = 7;
const DEFAULT_MAX_HOUR = 14;

/**
 * Opening hours of the weekday `isoDate` falls on. A closed day falls back to
 * the whole week, since staff can still book it and need a sensible window.
 */
export function clinicHoursForDate<
  T extends { day_of_week: DayOfWeek; is_closed?: boolean },
>(
  clinicHours: T[],
  isoDate: string,
): T[] {
  const weekday = DAY_ORDER[DateTime.fromISO(isoDate).weekday - 1];
  const dayHours = clinicHours.filter(
    (h) => h.day_of_week === weekday && !h.is_closed,
  );
  return dayHours.length > 0 ? dayHours : clinicHours;
}

/** Hour part of "HH:mm" / "HH:mm:ss", rounded up when minutes are set. */
function parseHour(time: string, roundUp: boolean): number {
  const [h, m = "0"] = time.split(":");
  return Number(h) + (roundUp && Number(m) > 0 ? 1 : 0);
}

/**
 * Calculates a tight slotMinTime / slotMaxTime for FullCalendar based on
 * the actual event times. The guaranteed base window is the clinic's opening
 * hours padded by an hour on each side, or 07:00–14:00 when none are set.
 */
export function calcCalendarTimeRange(
  events: Array<{ start: string; end: string }>,
  tz = "Europe/Bratislava",
  clinicHours: Array<{
    open_time: string;
    close_time: string;
    is_closed?: boolean;
  }> = [],
): { min: string; max: string } {
  let baseMin = DEFAULT_MIN_HOUR;
  let baseMax = DEFAULT_MAX_HOUR;

  const openHours = clinicHours.filter(
    (h) => !h.is_closed && h.open_time && h.close_time,
  );
  if (openHours.length > 0) {
    const open = Math.min(...openHours.map((h) => parseHour(h.open_time, false)));
    const close = Math.max(...openHours.map((h) => parseHour(h.close_time, true)));
    baseMin = Math.max(0, open - 1);
    baseMax = Math.min(24, close + 1);
  }

  let earliestHour = baseMin;
  let latestHour = baseMax;

  events.forEach((event) => {
    const start = DateTime.fromISO(event.start, { zone: tz });
    const end = DateTime.fromISO(event.end, { zone: tz });
    if (start.hour < earliestHour) earliestHour = start.hour;
    const endHour = end.hour + (end.minute > 0 ? 1 : 0);
    if (endHour > latestHour) latestHour = endHour;
  });

  return {
    min: `${String(earliestHour).padStart(2, "0")}:00:00`,
    max: `${String(latestHour).padStart(2, "0")}:00:00`,
  };
}

function parseAnnouncementDate(date: string): DateTime | null {
  // ISO format: YYYY-MM-DD
  let dt = DateTime.fromISO(date);
  if (!dt.isValid) {
    // Legacy format: "16. 6."
    const parts = date.match(/(\d+)/g);
    if (parts && parts.length >= 2) {
      dt = DateTime.fromObject({ day: parseInt(parts[0]), month: parseInt(parts[1]) });
    }
  }
  return dt.isValid ? dt : null;
}

export function parseAnnouncementDateDisplay(date: string): { day: string; month: string } {
  const dt = parseAnnouncementDate(date);
  if (!dt) return { day: "—", month: "—" };
  return {
    day: String(dt.day),
    month: dt.setLocale("sk").toFormat("LLL").toUpperCase(),
  };
}

/**
 * Newest first. Announcements are stored in the order they were added, so on
 * the same date the later-added one wins; unreadable dates go last.
 */
export function sortAnnouncementsNewestFirst<T extends { date: string }>(
  items: T[]
): T[] {
  const millis = (item: T) =>
    parseAnnouncementDate(item.date)?.toMillis() ?? -Infinity;
  return [...items].reverse().sort((a, b) => millis(b) - millis(a));
}

/**
 * Inverse of sortAnnouncementsNewestFirst: takes the list as the admin shows
 * it (newest first, new items on top) and returns it oldest first, the order
 * it is stored in — so a reload shows exactly what was saved.
 */
export function announcementsForStorage<T extends { date: string }>(
  displayed: T[]
): T[] {
  return sortAnnouncementsNewestFirst([...displayed].reverse()).reverse();
}

export function formatNextSlot(
  isoString: string,
  tz = "Europe/Bratislava",
): string {
  const now = DateTime.now().setZone(tz);
  const slotTime = DateTime.fromISO(isoString, { zone: "utc" })
    .setZone(tz)
    .setLocale("sk");

  const timeStr = slotTime.toFormat("H:mm");

  // hasSame, not equals: equals also compares locale, and slotTime is "sk".
  if (slotTime.hasSame(now, "day")) {
    return `dnes o ${timeStr}`;
  } else if (slotTime.hasSame(now.plus({ days: 1 }), "day")) {
    return `zajtra o ${timeStr}`;
  } else {
    return `${slotTime.toFormat("d.M.yyyy")} o ${timeStr}`;
  }
}
